import { createContext, useCallback, useEffect, useState } from "react";

import { compareDates } from "../../utils";

interface Member {
  id: string;
  name: string;
}

interface PlannedDate {
  date: string;
  state: string;
}

interface FilterState {
  years?: number[];
  codes?: string[];
  members?: string[];
  projects?: string[];
  plannedDates?: string[];
  allYears?: number[];
  allMembers?: Member[];
  allProjects?: string[];
  allPlannedDates?: PlannedDate[];
  setFilters: (filters: FilterState) => void;
  setYears: (years: number[]) => void;
  setMembers: (ids: string[]) => void;
  setProjects: (codes: string[]) => void;
  setPlannedDates: (plannedDates: string[]) => void;
  setAllYears: (years: number[]) => void;
  setAllMembers: (members: Member[]) => void;
  setAllProjects: (projects: string[]) => void;
  setAllPlannedDates: (plannedDates: PlannedDate[]) => void;
  resetFilters: () => void;
}

export const FilterContext = createContext<FilterState>({
  setFilters: (filters: FilterState) => {
    /* noop */
  },
  setYears: (years: number[]) => {
    /* noop */
  },
  setMembers: (ids: string[]) => {
    /* noop */
  },
  setProjects: (codes: string[]) => {
    /* noop */
  },
  setPlannedDates: (plannedDates: string[]) => {
    /* noop */
  },
  setAllYears: (years: number[]) => {
    /* noop */
  },
  setAllMembers: (members: Member[]) => {
    /* noop */
  },
  setAllProjects: (projects: string[]) => {
    /* noop */
  },
  setAllPlannedDates: (plannedDates: PlannedDate[]) => {
    /* noop */
  },
  resetFilters: () => {
    /* noop */
  },
});

function FilterContextProvider({ children }: { children: any }) {
  const [filters, setFilters] = useState<{
    years?: number[];
    codes?: string[];
    members?: string[];
    projects?: string[];
    plannedDates?: string[];
  }>(() => {
    let f: FilterState = JSON.parse(sessionStorage.getItem("filters") || "{}");
    return f;
  });
  useEffect(() => {
    sessionStorage.setItem("filters", JSON.stringify(filters || {}));
  }, [filters]);
  const setYears = useCallback(
    (years: number[]) =>
      setFilters((f) => ({
        ...(f || {}),
        years: years,
      })),
    []
  );
  const setMembers = useCallback(
    (ids: string[]) =>
      setFilters((f) => ({
        ...(f || {}),
        members: ids?.filter((id) => !!id),
      })),
    []
  );
  const setProjects = useCallback(
    (codes: string[]) =>
      setFilters((f) => ({
        ...(f || {}),
        projects: codes,
      })),
    []
  );
  const setPlannedDates = useCallback(
    (plannedDates: string[]) =>
      setFilters((f) => ({
        ...(f || {}),
        plannedDates: plannedDates,
      })),
    []
  );

  const setAllYears = useCallback(
    (years: number[]) =>
      setFilters((f) => ({
        ...(f || {}),
        allYears: years?.sort() || [],
      })),
    []
  );
  const setAllMembers = useCallback(
    (members: Member[]) =>
      setFilters((f) => ({
        ...(f || {}),
        allMembers: members.sort((a, b) => a.name.localeCompare(b.name)),
      })),
    []
  );
  const setAllProjects = useCallback(
    (projects: string[]) =>
      setFilters((f) => ({
        ...(f || {}),
        allProjects: projects?.sort() || [],
      })),
    []
  );
  const setAllPlannedDates = useCallback(
    (plannedDates: PlannedDate[]) =>
      setFilters((f) => ({
        ...(f || {}),
        allPlannedDates:
          plannedDates
            .filter((date) => date.state === "PLANNED")
            .sort((plannedDateA, plannedDateB) =>
              compareDates(
                new Date(plannedDateA.date),
                new Date(plannedDateB.date)
              )
            ) || [],
      })),
    []
  );
  const resetFilters = useCallback(
    () =>
      setFilters((f) => ({
        ...(f || {}),
        members: [],
        years: [],
        projects: [],
        plannedDates: [],
      })),
    []
  );
  return (
    <FilterContext.Provider
      value={{
        ...filters,
        setFilters,
        setYears,
        setMembers,
        setProjects,
        setPlannedDates,
        setAllYears,
        setAllMembers,
        setAllProjects,
        setAllPlannedDates,
        resetFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export default FilterContextProvider;
